import React, { useState, useEffect } from 'react';
import {
  Table, Button, Badge, Modal, Form, Row, Col, Card, Alert,
} from 'react-bootstrap';
import {
  FaPlus, FaEye, FaCheck, FaCheckCircle, FaInfoCircle,
  FaExclamationTriangle, FaShieldAlt,
} from 'react-icons/fa';
import axios from 'axios';

/*
 * CCPA "Consent & Opt-Out" — list + record pattern (mirrors the DSAR tab).
 * Click "Record Opt-Out Request" → modal form → record is saved to
 * CcpaSaleOptOutRequest and appears in the list.
 *
 * Reuses the existing (now-unused) mechanism routes — no new API Gateway needed:
 *   POST /api/ccpa/opt-out-mechanisms/create        -> save opt-out request
 *   PUT  /api/ccpa/opt-out-mechanisms/update/{id}    -> update request status
 */
const API = {
  listRequests:
    'https://texkwwnqek.execute-api.ap-southeast-2.amazonaws.com/prod/api/ccpa/opt-out-mechanisms/list/requests',
  // reuse the existing (now-unused) mechanism create route
  createRequest:
    'https://uk32gatco3.execute-api.ap-southeast-2.amazonaws.com/prod/api/ccpa/opt-out-mechanisms/create',
  // reuse the existing (now-unused) mechanism update route
  updateRequest: (id) =>
    `https://cw3rsuk3y0.execute-api.ap-southeast-2.amazonaws.com/prod/api/ccpa/opt-out-mechanisms/update/${id}`,
  activities:
    'https://wjioewyrif.execute-api.ap-southeast-2.amazonaws.com/prod/api/ccpa/activities/create',
};

const CHANNELS = [
  '"Do Not Sell or Share" website link',
  'Global Privacy Control (GPC) signal',
  'Email',
  'Toll-free phone line',
  'Authorized agent',
  'In-person / mail',
];

const subTextStyle = {
  fontSize: '12.5px',
  color: '#6c757d',
  marginLeft: '1.6rem',
  marginTop: '2px',
  lineHeight: 1.45,
};

// add N business days to a date
const addBusinessDays = (start, days) => {
  const d = new Date(start);
  let added = 0;
  while (added < days) {
    d.setDate(d.getDate() + 1);
    const wd = d.getDay();
    if (wd !== 0 && wd !== 6) added += 1;
  }
  return d;
};

const isActioned = (status) =>
  status === 'Actioned' || status === 'Completed';

const CCPAOptOutConsent = ({
  userData,
  onScoreUpdate,
  showNotification,
  region,
  refreshActivities,
}) => {
  const headers = {
    Region: region,
    UserId: userData?.['cognito:username'],
  };

  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({ total: 0, open: 0, actioned: 0, overdue: 0 });
  const [showFormModal, setShowFormModal] = useState(false);
  const [viewing, setViewing] = useState(null);

  const logActivity = async (message, badge = 'info') => {
    try {
      await axios.post(
        API.activities,
        { action: badge, module: 'Opt-Out Requests', description: message },
        { headers }
      );
      if (refreshActivities) await refreshActivities();
    } catch (err) {
      console.error('❌ Error logging activity:', err);
    }
  };

  const computeStatsAndScore = (rows) => {
    const today = new Date();
    const total = rows.length;
    const actioned = rows.filter((r) => isActioned(r.status)).length;
    const overdue = rows.filter((r) => {
      if (isActioned(r.status) || !r.requestedAt) return false;
      return addBusinessDays(new Date(r.requestedAt), 15) < today;
    }).length;
    const open = total - actioned;
    setStats({ total, open, actioned, overdue });
    if (onScoreUpdate)
      onScoreUpdate(total === 0 ? 0 : Math.round((actioned / total) * 100));
  };

  const fetchRequests = async () => {
    try {
      const res = await axios.get(API.listRequests, { headers });
      const rows = Array.isArray(res.data) ? res.data : [];
      setRequests(rows);
      computeStatsAndScore(rows);
    } catch (error) {
      console.error('Error fetching opt-out requests:', error);
    }
  };

  useEffect(() => {
    if (userData?.['cognito:username']) fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  // ---- form state ----
  const gpcPresent =
    typeof navigator !== 'undefined' && navigator.globalPrivacyControl === true;

  const blankForm = {
    consumerId: '',
    caResident: true,
    rSale: gpcPresent,
    rShare: gpcPresent,
    rSensitive: false,
    channel: gpcPresent ? 'Global Privacy Control (GPC) signal' : '',
    noticeVer: '',
    agentToggle: false,
    agentName: '',
    agentProof: '',
    minorToggle: false,
    ageBand: '13-15',
    optInConfirm: false,
  };

  const [form, setForm] = useState(blankForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const openForm = () => {
    setForm(blankForm);
    setErrors({});
    setShowFormModal(true);
  };

  const validate = () => {
    const e = {};
    if (!form.consumerId.trim())
      e.consumerId = 'Enter the email or account ID so the request can be matched.';
    if (!form.channel) e.channel = 'Select how the request was received.';
    if (!form.noticeVer.trim())
      e.noticeVer = 'Record which notice the consumer was shown.';
    if (!form.rSale && !form.rShare && !form.rSensitive)
      e.rights = 'Select at least one opt-out right.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleRecordRequest = async () => {
    if (!validate()) return;

    const rightsList = [];
    if (form.rSale) rightsList.push('Do Not Sell');
    if (form.rShare) rightsList.push('Do Not Share');
    if (form.rSensitive) rightsList.push('Limit Sensitive PI');

    const payload = {
      consumerId: form.consumerId.trim(),
      californiaResident: form.caResident,
      rightsRequested: rightsList,
      optOutSale: form.rSale,
      optOutShare: form.rShare,
      limitSensitivePI: form.rSensitive,
      channel: form.channel,
      noticeVersion: form.noticeVer.trim(),
      gpcSignal: gpcPresent,
      authorizedAgent: form.agentToggle,
      authorizedAgentName: form.agentToggle ? form.agentName.trim() : '',
      authorizedAgentProof: form.agentToggle ? form.agentProof.trim() : '',
      minor: form.minorToggle,
      minorAgeBand: form.minorToggle ? form.ageBand : '',
      minorOptInRecorded: form.minorToggle ? form.optInConfirm : false,
      status: 'Recorded',
      requestedAt: new Date().toISOString(),
    };

    setSubmitting(true);
    try {
      await axios.post(API.createRequest, payload, { headers });
      await logActivity(`Recorded CCPA opt-out request: ${rightsList.join(', ')}`, 'success');
      showNotification('Opt-out request recorded successfully!');
      setShowFormModal(false);
      fetchRequests();
    } catch (error) {
      console.error('Error recording opt-out request:', error);
      showNotification('Error recording opt-out request', 'danger');
    } finally {
      setSubmitting(false);
    }
  };

  const markActioned = async (id) => {
    try {
      await axios.put(
        API.updateRequest(id),
        { status: 'Actioned', completedDate: new Date().toISOString() },
        { headers }
      );
      await logActivity(`Actioned CCPA opt-out request ${id}`, 'primary');
      showNotification('Opt-out request marked as actioned.');
      setViewing(null);
      fetchRequests();
    } catch (error) {
      console.error('Error updating request:', error);
      showNotification('Error updating request', 'danger');
    }
  };

  const statusVariant = (s) =>
    isActioned(s) ? 'success' : s === 'In Progress' ? 'primary' : 'secondary';

  const deadlineOf = (r) =>
    r.requestedAt ? addBusinessDays(new Date(r.requestedAt), 15) : null;

  const daysLeft = (r) => {
    const dl = deadlineOf(r);
    if (!dl) return null;
    return Math.ceil((dl - new Date()) / (1000 * 60 * 60 * 24));
  };

  const rightsDefs = [
    {
      id: 'rSale',
      title: 'Do Not Sell My Personal Information',
      sub: 'Stop the sale of personal information to third parties.',
    },
    {
      id: 'rShare',
      title: 'Do Not Share My Personal Information',
      sub: 'Stop sharing for cross-context behavioral advertising (added by CPRA).',
    },
    {
      id: 'rSensitive',
      title: 'Limit the Use of My Sensitive Personal Information',
      sub: "Restrict use of sensitive PI to what's necessary to provide the service.",
    },
  ];

  const sectionTitle = (text, extra = null, first = false) => (
    <div
      style={{
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        fontSize: '11px',
        fontWeight: 700,
        color: '#6c757d',
        lineHeight: 1.5,
        marginBottom: '14px',
        marginTop: first ? '8px' : '24px',
        paddingTop: first ? 0 : '16px',
        borderTop: first ? 'none' : '1px solid #e9ecef',
        clear: 'both',
      }}
    >
      {text} {extra}
    </div>
  );

  return (
    <>
      <Alert variant="info">
        <strong>CCPA / CPRA Opt-Out:</strong> California consumers can opt out of
        the sale or sharing of their personal information and limit use of
        sensitive PI. Opt-outs are non-verifiable and must be honored within
        15 business days.
      </Alert>

      <Row className="mb-3">
        <Col>
          <Button variant="primary" onClick={openForm}>
            <FaPlus /> Record Opt-Out Request
          </Button>
        </Col>
      </Row>

      {/* Stats */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h4>{stats.total}</h4>
              <small>Total Requests</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h4 className="text-secondary">{stats.open}</h4>
              <small>Open</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h4 className="text-success">{stats.actioned}</h4>
              <small>Actioned</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h4 className="text-danger">{stats.overdue}</h4>
              <small>Overdue</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* List */}
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>Request ID</th>
            <th>Rights</th>
            <th>Consumer</th>
            <th>Channel</th>
            <th>Requested</th>
            <th>Deadline (15 bus. days)</th>
            <th>Status</th>
            <th>Days Left</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {requests.length === 0 ? (
            <tr>
              <td colSpan={9} className="text-center text-muted">
                No opt-out requests yet. Click "Record Opt-Out Request" to add one.
              </td>
            </tr>
          ) : (
            requests.map((r) => {
              const dl = deadlineOf(r);
              const left = daysLeft(r);
              return (
                <tr key={r.id}>
                  <td className="small">#{r.id}</td>
                  <td className="small">
                    {Array.isArray(r.rightsRequested)
                      ? r.rightsRequested.join(', ')
                      : '—'}
                  </td>
                  <td className="small">{r.consumerId}</td>
                  <td className="small">{r.channel}</td>
                  <td className="small">
                    {r.requestedAt ? new Date(r.requestedAt).toLocaleDateString() : '—'}
                  </td>
                  <td className="small">{dl ? dl.toLocaleDateString() : '—'}</td>
                  <td>
                    <Badge bg={statusVariant(r.status)}>{r.status}</Badge>
                  </td>
                  <td>
                    {!isActioned(r.status) && left !== null && (
                      <span className={left < 0 ? 'text-danger fw-bold' : ''}>
                        {left} days
                      </span>
                    )}
                  </td>
                  <td>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      className="me-1"
                      onClick={() => setViewing(r)}
                    >
                      <FaEye />
                    </Button>
                    {!isActioned(r.status) && (
                      <Button
                        size="sm"
                        variant="outline-success"
                        onClick={() => markActioned(r.id)}
                      >
                        <FaCheck /> Action
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </Table>

      {/* ============ Record Opt-Out form modal ============ */}
      <Modal show={showFormModal} onHide={() => setShowFormModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Record CCPA / CPRA Opt-Out Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {gpcPresent ? (
            <Alert variant="success" className="d-flex">
              <FaCheckCircle className="me-2 mt-1 flex-shrink-0" />
              <div>
                <strong>Global Privacy Control signal detected.</strong> Sale and
                Share opt-outs are pre-selected and must be honored.
              </div>
            </Alert>
          ) : (
            <Alert variant="info" className="d-flex">
              <FaInfoCircle className="me-2 mt-1 flex-shrink-0" />
              <div>
                No GPC signal on this session. Record a manual opt-out below. CPRA
                requires GPC signals to be honored automatically when present.
              </div>
            </Alert>
          )}

          {/* Consumer */}
          {sectionTitle('Consumer', null, true)}
          <Form.Group className="mb-3">
            <Form.Label>
              Consumer identifier <span className="text-primary">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={form.consumerId}
              isInvalid={!!errors.consumerId}
              placeholder="Email or account ID used to match the record"
              onChange={(e) => set('consumerId', e.target.value)}
            />
            <Form.Control.Feedback type="invalid">{errors.consumerId}</Form.Control.Feedback>
          </Form.Group>
          <div style={{ marginBottom: '8px' }}>
            <Form.Check
              type="checkbox"
              id="caResident"
              checked={form.caResident}
              onChange={(e) => set('caResident', e.target.checked)}
              label="Consumer is a California resident"
            />
            <div style={subTextStyle}>
              CCPA/CPRA applies to California residents. Uncheck if jurisdiction differs.
            </div>
          </div>

          {/* Rights */}
          {sectionTitle(
            'Opt-out rights requested',
            <span style={{ color: '#dc3545', fontWeight: 400, textTransform: 'none' }}>
              (select at least one)
            </span>
          )}
          {rightsDefs.map((r) => (
            <div
              key={r.id}
              className={`border rounded ${form[r.id] ? 'border-primary' : ''}`}
              style={{ padding: '10px 12px', marginBottom: '10px' }}
            >
              <Form.Check
                type="checkbox"
                id={r.id}
                checked={form[r.id]}
                onChange={(e) => set(r.id, e.target.checked)}
                label={<span style={{ fontWeight: 600 }}>{r.title}</span>}
              />
              <div style={subTextStyle}>{r.sub}</div>
            </div>
          ))}
          {errors.rights && (
            <div className="text-danger small" style={{ marginBottom: '8px' }}>
              {errors.rights}
            </div>
          )}

          {/* Request details */}
          {sectionTitle('Request details')}
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Request channel <span className="text-primary">*</span>
                </Form.Label>
                <Form.Select
                  value={form.channel}
                  isInvalid={!!errors.channel}
                  onChange={(e) => set('channel', e.target.value)}
                >
                  <option value="">Select…</option>
                  {CHANNELS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">{errors.channel}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Notice / privacy policy version shown <span className="text-primary">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={form.noticeVer}
                  isInvalid={!!errors.noticeVer}
                  placeholder="e.g., Privacy Policy v3.2 (2026-01)"
                  onChange={(e) => set('noticeVer', e.target.value)}
                />
                <Form.Control.Feedback type="invalid">{errors.noticeVer}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          {/* Authorized agent */}
          {sectionTitle('Authorized agent')}
          <Form.Check
            type="checkbox"
            id="agentToggle"
            checked={form.agentToggle}
            onChange={(e) => set('agentToggle', e.target.checked)}
            label="This request was submitted by an authorized agent on the consumer's behalf"
          />
          {form.agentToggle && (
            <div className="border rounded" style={{ padding: '16px', marginTop: '10px' }}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Agent name / company</Form.Label>
                    <Form.Control
                      type="text"
                      value={form.agentName}
                      placeholder="Authorized agent"
                      onChange={(e) => set('agentName', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Proof of authorization</Form.Label>
                    <Form.Control
                      type="text"
                      value={form.agentProof}
                      placeholder="e.g., signed permission on file / power of attorney"
                      onChange={(e) => set('agentProof', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>
          )}

          {/* Minor status */}
          {sectionTitle('Minor status')}
          <Form.Check
            type="checkbox"
            id="minorToggle"
            checked={form.minorToggle}
            onChange={(e) => set('minorToggle', e.target.checked)}
            label="Consumer is under 16"
          />
          <div style={subTextStyle}>
            For minors, CCPA flips to opt-in — affirmative authorization is required
            rather than opt-out.
          </div>
          {form.minorToggle && (
            <div className="border rounded" style={{ padding: '16px', marginTop: '10px' }}>
              <Form.Group className="mb-3" style={{ maxWidth: 360 }}>
                <Form.Label>Age band</Form.Label>
                <Form.Select value={form.ageBand} onChange={(e) => set('ageBand', e.target.value)}>
                  <option value="13-15">13 to 15 — consumer's own opt-in required</option>
                  <option value="under13">Under 13 — parent/guardian opt-in required</option>
                </Form.Select>
              </Form.Group>
              <Alert variant="warning" className="d-flex py-2">
                <FaExclamationTriangle className="me-2 mt-1 flex-shrink-0" />
                <div>
                  {form.ageBand === 'under13' ? (
                    <>
                      Consumer is under 13. A <strong>parent or guardian</strong> must
                      provide affirmative opt-in. Opt-out alone is not sufficient.
                    </>
                  ) : (
                    <>
                      Consumer is 13–15. The{' '}
                      <strong>consumer's own affirmative opt-in</strong> is required
                      before sale or sharing.
                    </>
                  )}
                </div>
              </Alert>
              <Form.Check
                type="checkbox"
                id="optInConfirm"
                checked={form.optInConfirm}
                onChange={(e) => set('optInConfirm', e.target.checked)}
                label={
                  form.ageBand === 'under13'
                    ? 'Parent/guardian opt-in consent obtained and recorded'
                    : "Consumer's affirmative opt-in consent obtained and recorded"
                }
              />
            </div>
          )}

          {/* Identity verification */}
          {sectionTitle('Identity verification')}
          <Alert variant="light" className="border d-flex mb-0">
            <FaShieldAlt className="me-2 mt-1 flex-shrink-0 text-muted" />
            <div className="small text-muted">
              Identity verification is <strong>not required</strong> to honor an
              opt-out of sale or sharing — it is a non-verifiable request under CCPA.
              Do not block or delay pending verification.
            </div>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowFormModal(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleRecordRequest} disabled={submitting}>
            {submitting ? 'Recording…' : 'Record Opt-Out Request'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ============ View / action modal ============ */}
      <Modal show={!!viewing} onHide={() => setViewing(null)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Opt-Out Request — #{viewing?.id}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {viewing && (
            <Row className="small">
              <Col xs={5} className="text-muted">Consumer</Col>
              <Col xs={7} className="fw-semibold">{viewing.consumerId}</Col>
              <Col xs={5} className="text-muted">California resident</Col>
              <Col xs={7} className="fw-semibold">{viewing.californiaResident ? 'Yes' : 'No'}</Col>
              <Col xs={5} className="text-muted">Rights requested</Col>
              <Col xs={7} className="fw-semibold">
                {Array.isArray(viewing.rightsRequested) ? viewing.rightsRequested.join(', ') : '—'}
              </Col>
              <Col xs={5} className="text-muted">Channel</Col>
              <Col xs={7} className="fw-semibold">{viewing.channel}</Col>
              <Col xs={5} className="text-muted">GPC signal</Col>
              <Col xs={7} className="fw-semibold">{viewing.gpcSignal ? 'Yes' : 'No'}</Col>
              <Col xs={5} className="text-muted">Authorized agent</Col>
              <Col xs={7} className="fw-semibold">
                {viewing.authorizedAgent ? viewing.authorizedAgentName || 'Yes' : '—'}
              </Col>
              <Col xs={5} className="text-muted">Minor</Col>
              <Col xs={7} className="fw-semibold">
                {viewing.minor
                  ? `${viewing.minorAgeBand} — opt-in ${viewing.minorOptInRecorded ? 'recorded' : 'NOT recorded'}`
                  : 'No'}
              </Col>
              <Col xs={5} className="text-muted">Notice shown</Col>
              <Col xs={7} className="fw-semibold">{viewing.noticeVersion}</Col>
              <Col xs={5} className="text-muted">Requested</Col>
              <Col xs={7} className="fw-semibold">
                {viewing.requestedAt ? new Date(viewing.requestedAt).toLocaleString() : '—'}
              </Col>
              <Col xs={5} className="text-muted">Status</Col>
              <Col xs={7} className="fw-semibold">{viewing.status}</Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setViewing(null)}>Close</Button>
          {viewing && !isActioned(viewing.status) && (
            <Button variant="success" onClick={() => markActioned(viewing.id)}>
              <FaCheck /> Mark Actioned
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CCPAOptOutConsent;